import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { Plus, X, ShoppingCart, PackageSearch, Pencil, Undo2 } from "lucide-react";
import {
  api,
  fmtMoney,
  type PaymentMethod,
  type Product,
  type Purchase,
  type Supplier,
} from "../api";
import { fmtDateLong } from "../lib/format";
import { useBranch, useBranchLookup } from "../lib/use-branch";
import { BranchLabel } from "../components/branch-label";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { MoneyInput } from "../components/money-input";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { ProductCombobox } from "../components/product-combobox";
import { QtyStepper } from "../components/qty-stepper";
import { AutocompleteInput } from "../components/autocomplete-input";
import { PaymentMethodSelect } from "../components/payment-method-select";
import {
  PaymentMethodCorrectionDialog,
  PaymentMethodValue,
} from "../components/payment-method-correction";
import { PaymentEditDialog } from "../components/payment-edit-dialog";
import { ReturnDialog } from "../components/return-dialog";
import { DateColumn, LedgerDayTable, buildDateGroups } from "../components/date-ledger";
import { useFocusDoc } from "../lib/use-focus-doc";
import {
  buildReturnedIndex,
  docHasReturnable,
  fetchAllReturns,
  type ReturnedIndex,
} from "../lib/returns-index";
import { cn } from "../lib/utils";

// Xarid qatori — faqat mavjud tovar (yangi tovar Tovarlar oynasi orqali ochiladi).
type Line = {
  key: number;
  product: Product;
  miqdor: string;
  tannarx: string;
};

let lineSeq = 1;
const PAGE = 200;

function paymentLabel(t: TFunction, status: Purchase["tolov_holati"]): string {
  return status === "tolandi"
    ? t("xaridlar.tolov.tolandi")
    : status === "qisman"
      ? t("xaridlar.tolov.qisman")
      : t("xaridlar.tolov.tolanmagan");
}

function PaymentBadge({ status, t }: { status: Purchase["tolov_holati"]; t: TFunction }) {
  const variant = status === "tolandi" ? "success" : status === "qisman" ? "secondary" : "destructive";
  return <Badge variant={variant}>{paymentLabel(t, status)}</Badge>;
}

export function XaridlarPage({
  initialProduct,
  openSignal,
  focusDoc,
}: {
  initialProduct?: Product | null;
  openSignal?: number;
  /** Tovar jurnalidan kelingan hujjat — tanlanadi va ajratib ko'rsatiladi. */
  focusDoc?: { id: number; seq: number } | null;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  // Topbar'dagi filial selektori shu sahifada ham amal qiladi: ustun bilan
  // yonma-yon turgan selektor ishlamasa, qaysi biri haqiqat ekani noaniq
  // bo'lardi. Yagona filialli ustaxonada `showSelector` false — ustun ham,
  // filtr ham yo'q.
  const { selectedBranchId, showSelector } = useBranch();
  const branchOf = useBranchLookup();
  const [items, setItems] = useState<Purchase[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [creating, setCreating] = useState(false);
  const [payEdit, setPayEdit] = useState<Purchase | null>(null);
  // Usulni to'g'rilash — to'lov holatidan QAT'I NAZAR ochiladi (to'liq
  // to'langan xaridda `payEdit` umuman ko'rinmaydi).
  const [methodEdit, setMethodEdit] = useState<Purchase | null>(null);
  const [returnDoc, setReturnDoc] = useState<Purchase | null>(null);
  const [retIndex, setRetIndex] = useState<ReturnedIndex>(new Map());
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const pg = await api.purchases({
          page: 1,
          page_size: PAGE,
          branch_id: selectedBranchId ?? undefined,
        });
        // Filial almashsa ro'yxat NOLDAN quriladi — "ko'proq yuklash"
        // to'plagichi ikki filialning hujjatlarini aralashtirib yubormasin.
        setItems(pg.items);
        setTotal(pg.total);
        setPage(1);
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("common:common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh, selectedBranchId, t]);

  // Qaytarilgan miqdorlar indeksi — "qaytariladigan qoldiq bormi" tugma holati
  // uchun. Yuklab bo'lmasa indeks bo'sh qoladi (tugma faol — backend baribir
  // tekshiradi).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const docs = await fetchAllReturns("purchase_return");
        if (alive) setRetIndex(buildReturnedIndex(docs));
      } catch {
        /* empty */
      }
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (initialProduct) setCreating(true);
  }, [initialProduct]);

  // Tablar qatoridagi "Yangi xarid" tugmasi (OmborPage) signal orqali ochadi.
  useEffect(() => {
    if (openSignal) setCreating(true);
  }, [openSignal]);

  // Hujjatlarni MAHALLIY sana bo'yicha guruhlaymiz (ko'rsatiladigan sana bilan mos).
  // Jurnaldan kelingan hujjat — ro'yxatga QO'SHIB beriladi (uning holatiga
  // yozilmaydi), shuning uchun qayta yuklash uni yuvib yubormaydi.
  const { items: shownItems, highlightId } = useFocusDoc<Purchase>({
    focusDoc,
    items,
    fetchOne: api.getPurchase,
    onSelectDate: setSelectedDate,
  });

  const groups = useMemo(
    () => buildDateGroups(shownItems, (p) => p.created_at, (p) => Number(p.net_total ?? p.jami_summa)),
    [shownItems],
  );

  // Standart tanlov = eng yangi (ma'lumotli) sana.
  useEffect(() => {
    if (groups.length && (selectedDate === null || !groups.some((g) => g.key === selectedDate))) {
      setSelectedDate(groups[0].key);
    }
  }, [groups, selectedDate]);

  const selectedGroup = groups.find((g) => g.key === selectedDate) ?? null;
  const hasMore = items.length < total;

  async function loadMore() {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const pg = await api.purchases({
        page: next,
        page_size: PAGE,
        branch_id: selectedBranchId ?? undefined,
      });
      setItems((prev) => [...prev, ...pg.items]);
      setTotal(pg.total);
      setPage(next);
    } catch {
      /* empty */
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </CardContent>
        </Card>
      ) : shownItems.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={<ShoppingCart />}
              title={t("xaridlar.empty.title")}
              description={t("xaridlar.empty.desc")}
              action={<Button onClick={() => setCreating(true)}><Plus />{t("xaridlar.newPurchase")}</Button>}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
          <DateColumn
            totalLabel={t("shared.netReturn.net")}
            groups={groups.map((g) => ({
              key: g.key,
              total: g.total,
              count: g.items.length,
              repISO: g.repISO,
              // Qarz daftari kabi — faqat yetkazib beruvchili xaridlar qarzga kiradi.
              debt: g.items.reduce(
                (s, p) =>
                  s +
                  (p.supplier_id != null
                    ? Number(p.remaining_amount ?? Math.max(0, Number(p.jami_summa) - Number(p.tolangan_summa)))
                    : 0),
                0,
              ),
            }))}
            selectedKey={selectedDate}
            onSelect={setSelectedDate}
            labelOf={(iso) => fmtDateLong(iso)}
            countLabel={(n) => t("xaridlar.count", { count: n })}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadingMore={loadingMore}
            loadMoreLabel={t("shared.ledger.loadMore")}
          />

          <Card>
            <CardContent className="p-0">
              {selectedGroup && (
                <>
                  <div className="border-b p-3">
                    <div className="text-sm font-semibold">{fmtDateLong(selectedGroup.repISO)}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("xaridlar.count", { count: selectedGroup.items.length })} · {t("shared.netReturn.net")}: {fmtMoney(selectedGroup.total)}
                    </div>
                  </div>
                  <LedgerDayTable
                    docs={selectedGroup.items.map((p) => ({
                      id: p.id,
                      party: p.supplier_name || "—",
                      branch: showSelector ? (
                        <BranchLabel
                          name={branchOf(p.branch_id)?.name}
                          isActive={branchOf(p.branch_id)?.is_active}
                        />
                      ) : undefined,
                      timeISO: p.created_at,
                      badge: <PaymentBadge status={p.tolov_holati} t={t} />,
                      method: (
                        <PaymentMethodValue
                          usul={p.tolov_usuli}
                          payments={p.payments}
                          onCorrect={() => setMethodEdit(p)}
                        />
                      ),
                      total: Number(p.jami_summa),
                      remaining: p.remaining_amount,
                      economics: Number(p.returned_total) > 0 ? (
                        <div className="text-xs text-muted-foreground">
                          {t("shared.netReturn.original")}: {fmtMoney(p.jami_summa)} · {t("shared.netReturn.returned")}: {fmtMoney(p.returned_total!)}
                          <br />{t("shared.netReturn.net")}: {fmtMoney(p.net_total!)}
                        </div>
                      ) : undefined,
                      paid: Number(p.tolangan_summa),
                      highlighted: p.id === highlightId,
                      lines: p.lines,
                      actions: (
                        <div className="flex items-center justify-center gap-0.5">
                          {Number(p.remaining_amount ?? (Number(p.jami_summa) - Number(p.tolangan_summa))) > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() => setPayEdit(p)}
                              title={t("shared.paymentEdit.title")}
                              aria-label={t("shared.paymentEdit.title")}
                            >
                              <Pencil className="size-4" />
                            </Button>
                          )}
                          {(() => {
                            const returnable = docHasReturnable(p.id, p.lines, retIndex);
                            return (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn("size-8", !returnable && "opacity-60")}
                                onClick={() =>
                                  returnable
                                    ? setReturnDoc(p)
                                    : toast.info(t("shared.return.nothingToast"))
                                }
                                title={
                                  returnable
                                    ? t("shared.return.purchaseTitle")
                                    : t("shared.return.nothingReturnable")
                                }
                                aria-label={t("shared.return.action")}
                              >
                                <Undo2 className="size-4" />
                              </Button>
                            );
                          })()}
                        </div>
                      ),
                    }))}
                    headers={{
                      party: t("xaridlar.table.supplier"),
                      tovar: t("xaridlar.form.tovar"),
                      narx: t("xaridlar.form.tannarx"),
                      miqdor: t("xaridlar.form.miqdor"),
                      jami: t("xaridlar.form.jami"),
                      tolangan: t("xaridlar.table.tolangan"),
                      // Berilmasa ustun umuman chizilmaydi (`foyda` bilan bir
                      // xil naqsh) — yagona filialda bo'sh ustun qolmaydi.
                      branch: showSelector ? t("shared.ledger.branch") : undefined,
                      vaqt: t("xaridlar.table.vaqt"),
                      amal: t("xaridlar.table.amallar"),
                    }}
                    qarzLabel={t("xaridlar.form.qarz")}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={creating}
        onOpenChange={(o) => !o && setCreating(false)}
        contentClassName="max-w-2xl p-0"
      >
        {creating && (
          <XaridForm
            initialProduct={initialProduct ?? null}
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              setRefresh((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      {payEdit && (
        <PaymentEditDialog
          open={!!payEdit}
          onOpenChange={(o) => !o && setPayEdit(null)}
          jami={Number(payEdit.jami_summa)}
          initialPaid={Number(payEdit.tolangan_summa)}
          partyLabel={payEdit.supplier_name || undefined}
          onSave={async (body) => {
            await api.updatePurchasePayment(payEdit.id, body);
            setRefresh((x) => x + 1);
          }}
        />
      )}

      {methodEdit && (
        <PaymentMethodCorrectionDialog
          open={!!methodEdit}
          onOpenChange={(o) => !o && setMethodEdit(null)}
          current={methodEdit.tolov_usuli}
          partyLabel={methodEdit.supplier_name || undefined}
          onSave={async (usul) => {
            await api.correctPurchaseMethod(methodEdit.id, usul);
            setRefresh((x) => x + 1);
          }}
        />
      )}

      {returnDoc && (
        <ReturnDialog
          open={!!returnDoc}
          onOpenChange={(o) => !o && setReturnDoc(null)}
          kind="purchase_return"
          docId={returnDoc.id}
          partyLabel={returnDoc.supplier_name || "—"}
          lines={returnDoc.lines}
          onDone={() => setRefresh((x) => x + 1)}
        />
      )}
    </div>
  );
}

export function XaridForm({
  initialProduct,
  onCancel,
  onSaved,
}: {
  initialProduct: Product | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const [products, setProducts] = useState<Product[]>([]);
  const [lines, setLines] = useState<Line[]>(() =>
    initialProduct
      ? [{ key: lineSeq++, product: initialProduct, miqdor: "1", tannarx: initialProduct.tannarx }]
      : [],
  );
  // Yetkazib beruvchi — erkin matn + autocomplete (shu ustaxona nomlari).
  // Bo'sh → anonim; mos kelsa → mavjud; mos kelmasa → yangi (faqat nom).
  const [supName, setSupName] = useState("");
  const [supResults, setSupResults] = useState<Supplier[]>([]);
  const [izoh, setIzoh] = useState("");
  // To'langan summa — qarz manbasi. Tahrirlanmaguncha jamiga teng.
  const [paid, setPaid] = useState("");
  const [paidTouched, setPaidTouched] = useState(false);
  // Yetkazib beruvchiga pul QANDAY berilgani. Ixtiyoriy.
  // Sukut bo'yicha naqd: bu yerda odam aynan hozir to'lovni yozyapti. Bo'sh
  // ("yozilmagan") tanlov ro'yxatda qoladi — bir bosish bilan tanlanadi.
  const [usul, setUsul] = useState<PaymentMethod | "">("naqd");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setProducts(await api.products({ faol: true }));
      } catch { /* empty */ }
    })();
  }, []);

  // Yetkazib beruvchi takliflari — yozilganda debounce bilan (workshop-scoped).
  useEffect(() => {
    const q = supName.trim();
    if (!q) {
      setSupResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        // `faol=true` — nofaol qilingan yetkazib beruvchi yangi xaridda
        // taklif qilinmaydi (kartochkasi va tarixi esa joyida qoladi).
        setSupResults((await api.suppliers(q, true)).items);
      } catch {
        setSupResults([]);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [supName]);

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.miqdor) || 0) * (Number(l.tannarx) || 0), 0),
    [lines],
  );

  useEffect(() => {
    if (!paidTouched) setPaid(total ? String(total) : "");
  }, [total, paidTouched]);

  const paidNum = paidTouched ? Number(paid) || 0 : total;
  const qarz = Math.max(0, total - paidNum);
  const needSupplier = qarz > 0;

  const usedIds = useMemo(
    () => new Set(lines.map((l) => l.product.id)),
    [lines],
  );

  function setLine(key: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function addProduct(p: Product) {
    setLines((ls) =>
      ls.some((l) => l.product.id === p.id)
        ? ls
        : [
            ...ls,
            {
              key: lineSeq++,
              product: p,
              miqdor: "1",
              // Kasrni olib tashlaymiz ("10000.00" → "10000"), aks holda
              // MoneyInput nuqtani yo'qotib ×100 ko'rsatardi.
              tannarx: String(Math.round(Number(p.tannarx))),
            },
          ],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lines.length === 0) {
      setErr(t("xaridlar.form.atLeastOne"));
      return;
    }
    const typedSup = supName.trim();
    if (needSupplier && !typedSup) {
      setErr(t("xaridlar.form.debtNeedsSupplier"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const payloadLines = lines.map((l) => ({
        miqdor: l.miqdor || "0",
        tannarx: l.tannarx || "0",
        product_id: l.product.id,
      }));
      const body: Record<string, unknown> = {
        tolangan_summa: String(paidNum),
        // Nol to'lovda usul yuborilmaydi — pul harakat qilmagan.
        ...(usul && paidNum > 0 ? { tolov_usuli: usul } : {}),
        izoh: izoh || null,
        lines: payloadLines,
      };
      // Yetkazib beruvchini aniqlaymiz: bo'sh → yo'q; aniq mos kelsa → mavjud
      // (yangi nusxa yaratilmaydi); aks holda → yangi (faqat nom).
      //
      // Bu qidiruv taklif ro'yxatidan farqli o'laroq `faol` bo'yicha
      // FILTRLAMAYDI: nofaol yetkazib beruvchining nomi qo'lda kiritilsa,
      // faqat faollari ichidan qidirsak mos kelmasdi va "yangi" deb
      // yaratmoqchi bo'lardik — nom unikal bo'lgani uchun 409 bilan tugardi.
      if (!typedSup) {
        body.supplier_mode = "none";
      } else {
        const res = await api.suppliers(typedSup);
        const match = res.items.find(
          (s) => s.name.trim().toLowerCase() === typedSup.toLowerCase(),
        );
        if (match) {
          body.supplier_mode = "existing";
          body.supplier_id = match.id;
        } else {
          body.supplier_mode = "new";
          body.new_supplier = { name: typedSup };
        }
      }
      await api.createPurchase(body);
      toast.success(t("xaridlar.form.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex max-h-[85vh] w-full flex-col">
      {/* 1. Qotirilgan header: sarlavha + mavjud tovar qidiruvi */}
      <div className="flex-none space-y-3 border-b p-4 sm:p-5">
        <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">{t("xaridlar.form.title")}</h2>
        <ProductCombobox
          products={products}
          excludeIds={usedIds}
          onPick={addProduct}
          disabled={busy}
        />
      </div>

      {/* 2. Yagona scroll zonasi: tovarlar jadvali */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-[120px]" />
            <col className="w-[132px]" />
            <col className="w-[140px]" />
            <col className="w-[40px]" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
            <tr className="border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 text-left">{t("xaridlar.form.tovar")}</th>
              <th className="px-2 py-2 text-center">{t("xaridlar.form.miqdor")}</th>
              <th className="px-2 py-2 text-right">{t("xaridlar.form.tannarx")}</th>
              <th className="px-3 py-2 text-right">{t("xaridlar.form.jami")}</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-muted-foreground">
                  <PackageSearch className="mx-auto mb-2 size-6 opacity-60" />
                  {t("xaridlar.form.emptyHint")}
                </td>
              </tr>
            ) : (
              lines.map((l) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="px-3 py-2">
                    <div className="truncate font-medium" title={l.product.nomi}>{l.product.nomi}</div>
                    <div className="truncate text-xs text-muted-foreground">{Number(l.product.joriy_ostatka)} {l.product.birlik}</div>
                  </td>
                  <td className="px-2 py-2 text-center align-top">
                    <QtyStepper value={l.miqdor} onChange={(v) => setLine(l.key, { miqdor: v })} disabled={busy} />
                  </td>
                  <td className="px-2 py-2 align-top">
                    <MoneyInput value={l.tannarx} onChange={(v) => setLine(l.key, { tannarx: v })} disabled={busy} className="h-8 px-2 text-right tabular-nums" />
                  </td>
                  <td className="px-3 py-2 text-right align-top font-semibold tabular-nums">
                    {fmtMoney((Number(l.miqdor) || 0) * (Number(l.tannarx) || 0))}
                  </td>
                  <td className="px-2 py-2 text-center align-top">
                    <button type="button" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} disabled={busy} className="text-muted-foreground hover:text-destructive disabled:opacity-50" aria-label={t("shared.delete")}>
                      <X className="size-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {err && (
          <div className="m-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div>
        )}
      </div>

      {/* 3. Qotirilgan footer: yetkazib beruvchi + to'langan + Jami/Qarz + tugmalar */}
      <div className="flex-none space-y-3 rounded-b-lg border-t bg-muted/30 p-4 sm:p-5">
        {/* Yetkazib beruvchi — erkin matn + autocomplete (bo'sh = anonim). */}
        <div className="space-y-1">
          <AutocompleteInput
            value={supName}
            onChange={setSupName}
            suggestions={supResults.map((s) => s.name)}
            placeholder={t("xaridlar.form.supplierPlaceholder")}
            disabled={busy}
          />
          {needSupplier && !supName.trim() && (
            <p className="text-xs text-destructive">{t("xaridlar.form.debtNeedsSupplier")}</p>
          )}
        </div>
        <Input value={izoh} onChange={(e) => setIzoh(e.target.value)} disabled={busy} placeholder={t("xaridlar.form.izohPlaceholder")} />

        <div className="flex items-end justify-between gap-3">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">{t("xaridlar.form.tolangan")}</label>
            <MoneyInput
              value={paid}
              onChange={(v) => { setPaidTouched(true); setPaid(v); }}
              disabled={busy}
              className="h-9 w-40 text-right tabular-nums"
              placeholder="0"
            />
            {/* Usul — nol to'lovda o'chiriladi (server ham rad etadi). */}
            <PaymentMethodSelect
              id="x-usul"
              value={usul}
              onChange={setUsul}
              disabled={busy || paidNum <= 0}
              className="w-40 space-y-1"
            />
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("xaridlar.form.jami")}</div>
            <div className="text-2xl font-bold tabular-nums">{fmtMoney(total)}</div>
            {qarz > 0 && (
              <div className="text-sm font-medium tabular-nums text-destructive">
                {t("xaridlar.form.qarz")}: {fmtMoney(qarz)}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-none">{t("sotuvlar.cancel")}</Button>
          <Button type="submit" disabled={busy || lines.length === 0} className="flex-1">
            {busy ? t("xaridlar.form.submitting") : t("xaridlar.form.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
}
